import { Button } from '@/components/ui/Button';
import { BriefView } from '@/components/profile/restitution/BriefView';
import { ThemeCards } from '@/components/profile/ThemeCards';
import { ProposalReadView } from '@/components/profile/QualificationReadView';
import { briefIsStale, linesById, pageIsStale } from '@/lib/company-restitution';
import { type CompanyQualificationSheet, QUALIFICATION_THEMES, type QualificationThemeKey } from '@shared/types/company-qualification';

export interface RestitutionWriting {
  running: boolean;
  /** Parts announced by the server, and those already written (true) or failed (false). */
  targets: string[];
  done: Record<string, boolean>;
  error: string | null;
}

interface Props {
  sheet: CompanyQualificationSheet;
  writing: RestitutionWriting | null;
  /** Unsaved changes in the sheet: writing would ignore them. */
  sheetDirty: boolean;
  onWrite: () => void;
  onOpenTheme: (theme: QualificationThemeKey) => void;
  onOpenSheet: () => void;
}

/** What the progress line says: only parts the server announced and finished. */
export const writingStatus = (writing: RestitutionWriting): string => {
  const finished = Object.keys(writing.done).length;
  return writing.targets.length
    ? `Rédaction de la restitution : ${finished} partie${finished > 1 ? 's' : ''} sur ${writing.targets.length} prête${finished > 1 ? 's' : ''}…`
    : 'Rédaction de la restitution…';
};

/** The result of the qualification: the answer, the themes as cards (each opens its page), then the whole proposal. */
export const RestitutionView = ({ sheet, writing, sheetDirty, onWrite, onOpenTheme, onOpenSheet }: Props) => {
  const lines = linesById(sheet);
  const { brief, pages } = sheet.restitution;
  const themes = QUALIFICATION_THEMES.filter(({ key }) => pages[key]);
  // Out of date, or a theme with lines that has no page yet (not written, or failed).
  const missing = QUALIFICATION_THEMES.some(({ key, sections }) => !pages[key] && sections.some((section) => sheet.sections[section].length > 0));
  const stale = briefIsStale(sheet) || missing || themes.some(({ key }) => pageIsStale(sheet, key));
  const running = writing?.running ?? false;
  return (
    <div className="min-w-0 space-y-6">
      {(brief ? stale : true) && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {sheetDirty && <p className="text-sm text-amber-800">Enregistrez vos modifications de la fiche avant de rédiger.</p>}
          <Button variant={brief ? 'secondary' : 'primary'} size="sm" onClick={onWrite} loading={running} disabled={running || sheetDirty}>
            {brief ? 'Mettre à jour la restitution' : 'Rédiger la restitution'}
          </Button>
        </div>
      )}
      {brief && <BriefView brief={brief} lines={lines} stale={briefIsStale(sheet)} deepThemes={QUALIFICATION_THEMES.filter(({ key }) => sheet.themes[key].depth === 'deep').length} />}
      <ThemeCards sheet={sheet} onOpen={onOpenTheme} onOpenSheet={onOpenSheet} />
      <div className="rounded-xl border border-domino-cardBorder bg-domino-warm px-6 py-6 sm:px-8">
        <ProposalReadView sheet={sheet} />
      </div>
    </div>
  );
};
